import { useState } from 'react';
import { Button } from '../ui/button';
import { CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Plus, Wrench, X } from 'lucide-react';
import { EquipmentMultiSelect } from '../job/JobFormRows';
import ShotReferences from './ShotReferences';
import ShotNumber from './ShotNumber';

function ShotEquipment({ shot, equipment, formatCurrency, handlers, crewMembers = [] }) {
  const [hireName, setHireName] = useState('');
  const items = shot.equipment || [];

  const addHire = () => {
    const name = hireName.trim();
    if (!name) return;
    handlers.addHire(shot.id, name);
    setHireName('');
  };

  return (
    <section className="space-y-4" aria-labelledby={`kit-${shot.id}`}>
      <h3 id={`kit-${shot.id}`} className="text-base flex items-center gap-2">
        <Wrench className="w-4 h-4" /> Equipment
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div className="space-y-2">
          <Label className="block truncate">Add from inventory</Label>
          <EquipmentMultiSelect
            equipment={equipment}
            selectedIds={items.map((e) => e.inventory_id).filter(Boolean)}
            onSelect={(id) => {
              const item = equipment.find((e) => e.id === id);
              if (item) handlers.addInventoryEquipment(shot.id, item);
            }}
            formatCurrency={formatCurrency}
          />
        </div>
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            addHire();
          }}
        >
          <Label htmlFor={`hire-${shot.id}`} className="block truncate">Add a hire</Label>
          <div className="flex gap-2">
            <Input
              id={`hire-${shot.id}`}
              placeholder="e.g. Water housing"
              value={hireName}
              onChange={(e) => setHireName(e.target.value)}
              data-testid="add-hire-input"
            />
            <Button type="submit" variant="outline" size="icon" disabled={!hireName.trim()} aria-label="Add hire">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>

      {/* A list, not a table: when the pane is narrow the controls wrap under
          the item's name instead of the row scrolling sideways. */}
      {items.length > 0 && (
        <ul className="rounded-cards border border-line divide-y divide-line" aria-label="Equipment for this shot">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
              <div className="flex-1 min-w-[11rem] space-y-1">
                <p className="text-sm leading-tight">{item.name}</p>
                {item.inventory_id ? (
                  <Badge variant="secondary" className="text-xs font-normal">Our inventory</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs font-normal">
                    {/* Who supplies it is chosen on the Sourcing tab. */}
                    Hire{item.supplier_id ? ` · ${crewMembers.find((c) => c.id === item.supplier_id)?.name || 'supplier'}` : ''}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-[2] min-w-[15rem]">
                <Label htmlFor={`qty-${item.id}`} className="text-xs text-muted-foreground font-normal">Qty</Label>
                <Input
                  id={`qty-${item.id}`}
                  type="number"
                  min="1"
                  aria-label={`Quantity of ${item.name}`}
                  value={item.quantity}
                  onChange={(e) => handlers.updateEquipment(shot.id, item.id, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                  className="h-8 w-16 shrink-0"
                />
                <Input
                  aria-label={`Note for ${item.name}`}
                  placeholder="Note"
                  value={item.note || ''}
                  onChange={(e) => handlers.updateEquipment(shot.id, item.id, { note: e.target.value })}
                  className="h-8 flex-1 min-w-0"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${item.name}`}
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handlers.removeEquipment(shot.id, item.id)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const TEXT_FIELDS = [
  { key: 'framing', label: 'Framing', placeholder: 'Wide' },
  { key: 'movement', label: 'Movement', placeholder: 'Push in' },
  { key: 'location', label: 'Location', placeholder: 'Beach' },
];

/** The right column: one shot, every field of it, and its equipment. */
/** Its add-setup, duplicate and delete live in the shot list's header, beside Add shot. */
export default function TreatmentShotEditor({ shot, number, equipment, formatCurrency, handlers, crewMembers }) {
  const set = (patch) => handlers.updateShot(shot.id, patch);

  return (
    <section aria-label={`Shot ${number}`}>
      {/* Pinned to the top of the editor's pane as the shot scrolls */}
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 h-14 px-6 py-0 border-b border-line sticky top-0 z-10 bg-card">
        {/* The title is edited in place, so it needs no field of its own */}
        <CardTitle className="flex items-center gap-2 min-w-0 flex-1">
          <ShotNumber number={number} />
          <Input
            aria-label="Shot title"
            value={shot.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Untitled shot"
            className="h-9 -ml-2 px-3 text-lg border-transparent shadow-none bg-transparent hover:bg-mist focus-visible:bg-mist"
            data-testid="shot-title-input"
          />
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* The frames lead: they're what the shot looks like */}
        <ShotReferences shot={shot} number={number} handlers={handlers} />

        <div className="border-t border-line pt-6 space-y-4">
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_5.5rem]">
            {TEXT_FIELDS.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label htmlFor={`${f.key}-${shot.id}`}>{f.label}</Label>
                <Input
                  id={`${f.key}-${shot.id}`}
                  value={shot[f.key] || ''}
                  onChange={(e) => set({ [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label htmlFor={`duration-${shot.id}`}>Seconds</Label>
              <Input
                id={`duration-${shot.id}`}
                type="number"
                min="0"
                step="0.5"
                value={shot.duration_seconds ?? ''}
                onChange={(e) => set({ duration_seconds: e.target.value === '' ? null : Number(e.target.value) || 0 })}
                placeholder="6"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`desc-${shot.id}`}>What we're thinking</Label>
            <Textarea
              id={`desc-${shot.id}`}
              rows={3}
              value={shot.description || ''}
              onChange={(e) => set({ description: e.target.value })}
              data-testid="shot-description-input"
            />
          </div>
        </div>

        <div className="border-t border-line pt-6">
          <ShotEquipment shot={shot} equipment={equipment} formatCurrency={formatCurrency} handlers={handlers} crewMembers={crewMembers} />
        </div>
      </CardContent>
    </section>
  );
}
